import {
  HABITAT_INTACTNESS_KEY,
  HabitatIntactnessAndThreats,
  LandscapeType,
  MAP_LAYER_FACTS,
  ThreatType,
} from '@/api/rest/resources/types/fact';
import { LANDLER_CAT_TO_OSM_CATEGORY } from '@/api/rest/resources/types/geoserver';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { IndigenousLands } from '@/api/rest/resources/types/indigenousAreas';
import {
  PROTECTED_AREAS_IBAT_COLORS,
  PROTECTED_AREAS_OSM_COLORS,
  ProtectedAreasIbat,
  ProtectedAreasOsm,
} from '@/api/rest/resources/types/protectedAreas';
import { IUCN_CATEGORY_COLORS, IUCNRedListCategory } from '@/api/rest/resources/types/species';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getMapBinaryColorForThreat } from '@/utils/getColorForThreat';
import { getLandscapeTypeColor } from '@/utils/getLandscapeTypeColor';

import { getIucnGetLevelThreeEFGLegendValues, getIucnGetLevelTwoBiomeLegendValues } from './utils/getLegends';

export const MAP_ORIGIN = {
  cckp: 'cckp',
  soilgrids: 'soilgrids',
  gbif: 'gbif',
  landscape: 'landscape',
  terrascope: 'terrascope',
  none: 'none',
  fact: 'fact',
  eii: 'eii',
  population_density: 'population_density',
  esa: 'esa',
  deforestation: 'deforestation',
  // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
  landler: 'landler',
} as const;

export const MAP_TYPE = {
  raster: 'raster',
  geojson: 'geojson',
  inSitu: 'inSitu',
} as const;

export const LEGEND_TYPE = {
  category: 'category',
  numeric: 'numeric',
} as const;

export const LANDSCAPE_VIEW_LAYERS: MAP_LAYER_FACTS[] = [
  MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
  MAP_LAYER_FACTS.opendata_protected_areas,
  MAP_LAYER_FACTS.iucn_protected_areas,
  // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
  MAP_LAYER_FACTS.siemens_protected_areas,
];

export const LANDCOVER_VIEW_LAYERS: MAP_LAYER_FACTS[] = [MAP_LAYER_FACTS.combined_ibat_lulc_urls];

export const SUBSCRIBED_LAYERS = [
  MAP_LAYER_FACTS.ecosystem_integrity_index_map_url,
  MAP_LAYER_FACTS.habitat_intactness_raster_urls,
  MAP_LAYER_FACTS.indicative_lands,
  MAP_LAYER_FACTS.indigenous_lands,
  MAP_LAYER_FACTS.population_density,
  MAP_LAYER_FACTS.esa_above_ground_carbon_map_url,
  MAP_LAYER_FACTS.deforestation_map_url,
  MAP_LAYER_FACTS.marine_habitat_map_url,
  MAP_LAYER_FACTS.marine_operations_map_url,
  MAP_LAYER_FACTS.iucn_get_level_2_biome_raster_asset,
  MAP_LAYER_FACTS.iucn_get_level_3_efg_raster_asset,
];

export type LegendConfig =
  | {
      type: typeof LEGEND_TYPE.category;
      className?: string;
      values: {
        text: string;
        color: string;
        tooltip?: string;
        filterValue?: string;
      }[];
      unit?: undefined;
      pattern?: string;
      filterKey?: string;
      hasLegendFilter?: boolean;
      layerLabel?: MAP_LAYER_FACTS;
    }
  | {
      type: typeof LEGEND_TYPE.numeric;
      values: string[];
      unit: string | undefined;
      pattern?: string;
      layerLabel?: MAP_LAYER_FACTS;
    };

export type RasterProps = {
  type: 'circle' | 'fill' | 'line' | 'symbol';
  colors?: Record<string, string>;
  dataKey?: string;
  color?: string;
  width?: number;
};

export type MapFilter = {
  label: string;
  filterKey: string;
  filterValues: string[];
  filterOperator: 'IN';
};

export type LayerProps = {
  value: MAP_LAYER_FACTS;
  origin: (typeof MAP_ORIGIN)[keyof typeof MAP_ORIGIN];
  legends?: LegendConfig[];
  type?: (typeof MAP_TYPE)[keyof typeof MAP_TYPE];
  raster?: RasterProps;
  isTimeSeries: boolean;
  timeSeriesStart?: string;
  timeSeriesEnd?: string;
  timeSeriesStepSize?: string;
  layerSubType?: HabitatIntactnessAndThreats;
  layerFilter?: MapFilter;
};

const LIST_LEGEND_STYLE = 'flex-col flex-nowrap';
const LEGEND_WITH_FILTER_STYLE = 'flex-row !gap-2 max-w-[250px]';

// TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
export const SIEMENS_HACK_PROTECTED_AREAS_LAYER = {
  value: MAP_LAYER_FACTS.siemens_protected_areas,
  origin: MAP_ORIGIN.landler,
  raster: {
    type: 'line' as const,
    color: '#FFFF00',
    width: 2,
  },
  type: MAP_TYPE.geojson,
  isTimeSeries: false,
  legends: [
    {
      type: LEGEND_TYPE.category,
      values: [
        {
          text: 'shared.ncaDetail.details.protectedAreas.labels.protectedAreas',
          color: '#FFFF00',
        },
      ],
    },
  ],
};

const LANDCOVER_LEGEND_ITEMS = Object.values(LandscapeType).map((category) => ({
  text: `global.landscapeTypes.${category}`,
  color: getLandscapeTypeColor(category),
}));

export const IBAT_LEGEND = {
  type: LEGEND_TYPE.category,
  className: LEGEND_WITH_FILTER_STYLE,
  values: Object.values(ProtectedAreasIbat).map((category) => ({
    text: `global.protectedAreas.categories.${category}`,
    color: PROTECTED_AREAS_IBAT_COLORS[`${category}`],
    tooltip: `global.protectedAreas.description.${category}`,
    filterValue: category,
  })),
  filterKey: 'iucn_cat_norm',
  hasLegendFilter: true,
  layerLabel: MAP_LAYER_FACTS.iucn_protected_areas,
};

export const OSM_LEGEND = {
  type: LEGEND_TYPE.category,
  className: LEGEND_WITH_FILTER_STYLE,
  values: Object.keys(ProtectedAreasOsm).map((category) => ({
    text: `global.protectedAreas.categories.${category}`,
    color: PROTECTED_AREAS_OSM_COLORS[ProtectedAreasOsm[category as keyof typeof ProtectedAreasOsm]],
    tooltip: `global.protectedAreas.description.${category}`,
    filterValue: Object.entries(LANDLER_CAT_TO_OSM_CATEGORY).find(
      ([, osmCat]) => osmCat === ProtectedAreasOsm[category as keyof typeof ProtectedAreasOsm],
    )?.[0],
  })),
  layerLabel: MAP_LAYER_FACTS.opendata_protected_areas,
  filterKey: 'class_priority',
  hasLegendFilter: true,
};

export const LAYER_OPTIONS: LayerProps[] = [
  { value: MAP_LAYER_FACTS.unknown, origin: MAP_ORIGIN.none, isTimeSeries: false },
  {
    value: MAP_LAYER_FACTS.annual_surface_air_temperature,
    origin: MAP_ORIGIN.cckp,
    legends: [
      {
        type: LEGEND_TYPE.numeric,
        unit: '°C',
        pattern:
          'bg-[linear-gradient(to_right,#000080,_#0000CD,_#0000FF,_#00BFFF,_#00FFFF,_#7FFFD4,_#00FF00,_#ADFF2F,_#FFFF00,_#FFD700,_#FFA500,_#FF4500,_#FF0000)]',
        values: ['-53', '0', '34'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
  },
  {
    value: MAP_LAYER_FACTS.annual_precipitation,
    origin: MAP_ORIGIN.cckp,
    legends: [
      {
        type: LEGEND_TYPE.numeric,
        unit: 'mm',
        pattern:
          'bg-[linear-gradient(to_right,#FFFFFF,_#EDF8E9,_#C7E9B4,_#A1D99B,_#7FCDBB,_#4EB3D3,_#41B6C4,_#2C7FB8,_#1D91C0,_#225EA8,_#253494,_#1B237F,_#0C2C84)]',
        values: ['0.64', '1750', '29866'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
  },
  {
    value: MAP_LAYER_FACTS.ocs_0_30cm_mean,
    origin: MAP_ORIGIN.soilgrids,
    legends: [
      {
        // Source: https://maps.isric.org/mapserv?map=/map/ocs.map&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetLegendGraphic&FORMAT=image/png&LAYER=ocs_0-30cm_mean&SLD_VERSION=1.1.0
        type: LEGEND_TYPE.numeric,
        unit: 't/ha',
        pattern:
          'bg-[linear-gradient(to_right,#ffffc2,_#fedf97,_#8b9d54,_#378944,_#21817c,_#1c3371,_#3b1271,_#3b1271)]',
        values: ['0', '105', '212'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.sand_0_5cm_mean,
    origin: MAP_ORIGIN.soilgrids,
    legends: [
      {
        // Source: https://maps.isric.org/mapserv?map=/map/sand.map&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetLegendGraphic&FORMAT=image/png&LAYER=sand_0-5cm_mean&SLD_VERSION=1.1.0
        type: LEGEND_TYPE.numeric,
        unit: 'g/kg',
        pattern:
          'bg-[linear-gradient(to_right,#f1dac3,_#e2bc99,_#cb905a,_#b27a47,_#9d6737,_#8a5729,_#7d4c20,_#75461b)]',
        values: ['0', '364', '799'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.cec_0_5cm_mean,
    origin: MAP_ORIGIN.soilgrids,
    legends: [
      {
        // Source: https://maps.isric.org/mapserv?map=/map/cec.map&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetLegendGraphic&FORMAT=image/png&LAYER=cec_0-5cm_mean&SLD_VERSION=1.1.0
        type: LEGEND_TYPE.numeric,
        unit: 'mmol(c)/kg',
        pattern:
          'bg-[linear-gradient(to_right,#edf8fb,_#d0e2ee,_#aec4de,_#95a1ca,_#8a79b6,_#8d51a7,_#ae30b4,_#c718bf)]',
        values: ['0', '229', '852'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.nitrogen_0_5cm_mean,
    origin: MAP_ORIGIN.soilgrids,
    legends: [
      {
        // Source: https://maps.isric.org/mapserv?map=/map/nitrogen.map&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetLegendGraphic&FORMAT=image/png&LAYER=nitrogen_0-5cm_mean&SLD_VERSION=1.1.0
        type: LEGEND_TYPE.numeric,
        unit: 'cg/kg',
        pattern:
          'bg-[linear-gradient(to_right,#f7fcf5,_#e7f6e2,_#bfe6ba,_#8fcf8e,_#49ac63,_#278947,_#1c833e,_#127d36)]',
        values: ['0', '620', '1237'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.gbif_species_occurrence,
    origin: MAP_ORIGIN.gbif,
    legends: [
      {
        // Source: https://techdocs.gbif.org/en/openapi/v2/maps#/Occurrence%20maps/getDensityTile
        type: LEGEND_TYPE.numeric,
        unit: undefined,
        pattern: 'bg-[linear-gradient(to_right,#fed976ff,_#fd8d3ccd,_#fd8d3cb3,_#f03b2099,_#bd002699)]',
        values: ['1-10', '101–1000', '100001+'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
    origin: MAP_ORIGIN.landscape,
    legends: [
      {
        type: LEGEND_TYPE.category,
        filterKey: 'iucnRedListCategory',
        values: Object.values(IUCNRedListCategory).map((category) => ({
          text: `global.iucn.category.${category}`,
          color: IUCN_CATEGORY_COLORS[`${category}`],
          tooltip: `global.iucn.description.${category}`,
          filterValue: category,
        })),
        hasLegendFilter: true,
      },
    ],
    type: MAP_TYPE.geojson,
    raster: {
      type: 'circle',
      colors: IUCN_CATEGORY_COLORS as Record<string, string>,
      dataKey: 'iucnRedListCategory',
    },
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.opendata_protected_areas,
    origin: MAP_ORIGIN.landscape,
    legends: [OSM_LEGEND],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.iucn_protected_areas,
    origin: MAP_ORIGIN.landscape,
    legends: [IBAT_LEGEND],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.esa_worldcover,
    origin: MAP_ORIGIN.terrascope,
    legends: [
      {
        type: LEGEND_TYPE.category,
        className: LIST_LEGEND_STYLE,
        values: [
          {
            text: 'global.plotTypes.tree_cover',
            color: '#006400',
          },
          {
            text: 'global.plotTypes.shrubland',
            color: '#ffbb22',
          },
          {
            text: 'global.plotTypes.grassland',
            color: '#ffff4c',
          },
          {
            text: 'global.plotTypes.cropland',
            color: '#f096ff',
          },
          {
            text: 'global.plotTypes.built_up',
            color: '#fa0000',
          },
          {
            text: 'global.plotTypes.bare_and_sparse_vegetation',
            color: '#b4b4b4',
          },
          {
            text: 'global.plotTypes.snow_and_ice',
            color: '#f0f0f0',
          },
          {
            text: 'global.plotTypes.permanent_water_bodies',
            color: '#1365c4',
          },
          {
            text: 'global.plotTypes.herbaceous_wetland',
            color: '#0096a0',
          },
          {
            text: 'global.plotTypes.mangroves',
            color: '#00cf75',
          },
          {
            text: 'global.plotTypes.moss_and_lichens',
            color: '#fae6a0',
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.combined_ibat_lulc_urls,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.category,
        className: LIST_LEGEND_STYLE,
        values: LANDCOVER_LEGEND_ITEMS,
        layerLabel: MAP_LAYER_FACTS.combined_ibat_lulc_urls,
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    timeSeriesStart: '2018-12-31',
    timeSeriesEnd: '2025-12-31',
    timeSeriesStepSize: 'yearly',
  },
  {
    value: MAP_LAYER_FACTS.habitat_intactness_raster_urls,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.numeric,
        unit: UnitEnum['%'],
        pattern: 'bg-[linear-gradient(to_right,#8B391E,_#F8EDB7)]',
        values: ['0', '25', '50', '75', '100'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    layerSubType: HABITAT_INTACTNESS_KEY,
  },
  {
    value: MAP_LAYER_FACTS.ecosystem_integrity_index_map_url,
    origin: MAP_ORIGIN.eii,
    legends: [
      {
        type: LEGEND_TYPE.numeric,
        unit: undefined,
        pattern:
          'bg-[linear-gradient(to_right,#ffffff,_#fffefe,_#fed8d6,_#eeb094,_#d0a35a,_#9a9132,_#647e43,_#396e59,_#1a5862,_#103f60,#011959)]',
        values: ['0', '0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1.0'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.indicative_lands,
    origin: MAP_ORIGIN.landscape,
    legends: [
      {
        type: LEGEND_TYPE.category,
        className: LIST_LEGEND_STYLE,
        values: [
          {
            text: 'shared.projects.project.heatMapLayers.indicative_lands.labels.indicativeLands',
            color: '#9C9C9C',
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.indigenous_lands,
    origin: MAP_ORIGIN.landscape,
    legends: [
      {
        type: LEGEND_TYPE.category,
        className: LIST_LEGEND_STYLE,
        values: [
          {
            text: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.abbreviations.${IndigenousLands.IndigenousPeoplesLandsAcknowledgedByGov}`,
            color: '#A63603',
            tooltip: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.description.${IndigenousLands.IndigenousPeoplesLandsAcknowledgedByGov}`,
          },
          {
            text: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.abbreviations.${IndigenousLands.IndigenousPeoplesLandsNotAcknowledgedByGov}`,
            color: '#FFDBA2',
            tooltip: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.description.${IndigenousLands.IndigenousPeoplesLandsNotAcknowledgedByGov}`,
          },
          {
            text: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.abbreviations.${IndigenousLands.LocalCommunityLandsAcknowledgedByGov}`,
            color: '#00478E',
            tooltip: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.description.${IndigenousLands.LocalCommunityLandsAcknowledgedByGov}`,
          },
          {
            text: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.abbreviations.${IndigenousLands.LocalCommunityLandsNotAcknowledgedByGov}`,
            color: '#C2E4FF',
            tooltip: `shared.ncaDetail.details.indigenousAreas.labels.indigenousLands.description.${IndigenousLands.LocalCommunityLandsNotAcknowledgedByGov}`,
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.population_density,
    origin: MAP_ORIGIN.population_density,
    legends: [
      {
        type: LEGEND_TYPE.numeric,
        unit: 'people/km²',
        pattern:
          'bg-[linear-gradient(to_right,#F4FBF2,_#D9F2E5,_#A8E3E5,_#71C7D7,_#428ACB,_#2D6BB3,_#0C4C9F,_#00309F,_#521F8B,_#700080,_#990049,_#CC003D,_#FF0000,_#FF6200,_#FF9E00)]',
        values: ['0.1k', '1k', '3.5k', '7.5k', '10k', '12k', '16k', '22k', '50k', '200k'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    timeSeriesStart: '2000-12-31',
    timeSeriesEnd: '2030-12-31',
  },
  {
    value: MAP_LAYER_FACTS.esa_above_ground_carbon_map_url,
    origin: MAP_ORIGIN.esa,
    legends: [
      {
        type: LEGEND_TYPE.numeric,
        unit: 't/ha',
        pattern: 'bg-[linear-gradient(to_right,#EDF8E9,_#BAE4B3,_#74C476,_#31A354,_#006D2C)]',
        values: ['1', '50', '100', '150', '200', '235'],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    timeSeriesStepSize: 'yearly',
    timeSeriesStart: '2018-12-31',
    timeSeriesEnd: '2022-12-31',
  },
  {
    value: MAP_LAYER_FACTS.deforestation_map_url,
    origin: MAP_ORIGIN.deforestation,
    legends: [
      {
        type: LEGEND_TYPE.category,
        values: [
          {
            text: 'global.deforestationCategory.forest_loss',
            color: '#C46A2C',
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },

  {
    value: MAP_LAYER_FACTS.marine_habitat_map_url,
    origin: MAP_ORIGIN.fact,
    type: MAP_TYPE.raster,
    isTimeSeries: false,
    legends: [
      {
        filterKey: 'Categories',
        hasLegendFilter: true,
        type: LEGEND_TYPE.category,
        values: [
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.zosteraSp',
            color: '#97FFA3',
            filterValue: 'Zostera sp.',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.posidoniaOceanica',
            color: '#6BD539',
            filterValue: 'Posidonia oceanica',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.cymodocea',
            color: '#BECF50',
            filterValue: 'Cymodocea',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.cymodoceaDiscontinuous',
            color: '#C9E24A',
            filterValue: 'Cymodocea Discontinuous',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.deadMatte',
            color: '#876950',
            filterValue: 'Dead matte',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.coarseSediment',
            color: '#A64D3C',
            filterValue: 'Coarse sediment',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.rock',
            color: '#824862',
            filterValue: 'Rock',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_habitat_map_url.labels.sand',
            color: '#E8D3B3',
            filterValue: 'Sand',
          },
        ],
      },
    ],
  },

  {
    value: MAP_LAYER_FACTS.marine_operations_map_url,
    origin: MAP_ORIGIN.fact,
    type: MAP_TYPE.raster,
    isTimeSeries: false,
    legends: [
      {
        type: LEGEND_TYPE.category,
        filterKey: 'Monitoring',
        hasLegendFilter: true,
        values: [
          {
            text: 'shared.projects.project.heatMapLayers.marine_operations_map_url.labels.degradedSite',
            color: '#FAA76B',
            filterValue: 'Degraded site',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_operations_map_url.labels.replantingArea',
            color: '#F41F0B',
            filterValue: 'Replanting area',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_operations_map_url.labels.goalSite',
            color: '#C8F902',
            filterValue: 'Goal site',
          },
          {
            text: 'shared.projects.project.heatMapLayers.marine_operations_map_url.labels.seaUrchinRemoval',
            color: '#936AC3',
            filterValue: 'Sea urchin removal',
          },
        ],
      },
    ],
    layerFilter: {
      label: 'Species',
      filterKey: 'Species',
      filterValues: ['Posidonia', 'Cystoseira'],
      filterOperator: 'IN',
    },
  },

  // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
  SIEMENS_HACK_PROTECTED_AREAS_LAYER,
  {
    type: MAP_TYPE.inSitu,
    value: MAP_LAYER_FACTS.in_situ_map_layer,
    origin: MAP_ORIGIN.landler,
    isTimeSeries: true,
    timeSeriesStepSize: 'yearly',
  },
  {
    value: MAP_LAYER_FACTS.iucn_get_level_2_biome_raster_asset,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.category,
        values: getIucnGetLevelTwoBiomeLegendValues(),
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
  {
    value: MAP_LAYER_FACTS.iucn_get_level_3_efg_raster_asset,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.category,
        values: getIucnGetLevelThreeEFGLegendValues(),
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: false,
  },
];

export const HABITAT_INTACTNESS_THREAT_LAYER_OPTIONS: LayerProps[] = [
  {
    value: MAP_LAYER_FACTS.habitat_intactness_raster_urls,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.category,
        values: [
          {
            text: 'global.threatTypes.deforestation',
            color: getMapBinaryColorForThreat(ThreatType.deforestation).presence,
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    layerSubType: ThreatType.deforestation,
  },
  {
    value: MAP_LAYER_FACTS.habitat_intactness_raster_urls,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.category,
        values: [
          {
            text: 'global.threatTypes.cropland',
            color: getMapBinaryColorForThreat(ThreatType.cropland).presence,
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    layerSubType: ThreatType.cropland,
  },
  {
    value: MAP_LAYER_FACTS.habitat_intactness_raster_urls,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.category,
        values: [
          {
            text: 'global.threatTypes.builtup',
            color: getMapBinaryColorForThreat(ThreatType.builtup).presence,
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    layerSubType: ThreatType.builtup,
  },
  {
    value: MAP_LAYER_FACTS.habitat_intactness_raster_urls,
    origin: MAP_ORIGIN.fact,
    legends: [
      {
        type: LEGEND_TYPE.category,
        values: [
          {
            text: 'global.threatTypes.roads',
            color: getMapBinaryColorForThreat(ThreatType.roads).presence,
          },
        ],
      },
    ],
    type: MAP_TYPE.raster,
    isTimeSeries: true,
    layerSubType: ThreatType.roads,
  },
];

export const CONSERVATION_UI_MAP_LAYERS = [
  MAP_LAYER_FACTS.species_presence_occurrence_location_geojson,
  MAP_LAYER_FACTS.ocs_0_30cm_mean,
  MAP_LAYER_FACTS.ecosystem_integrity_index_map_url,
  MAP_LAYER_FACTS.indicative_lands,
  MAP_LAYER_FACTS.indigenous_lands,
] as const as readonly MAP_LAYER_FACTS[];

export const MAP_LAYERS_BY_INDICATOR_PRODUCT = {
  [INDICATOR.CLEANWAVE]: [
    MAP_LAYER_FACTS.marine_habitat_map_url,
    MAP_LAYER_FACTS.marine_operations_map_url,
  ] as const as readonly MAP_LAYER_FACTS[],
};
